/* eslint-disable @next/next/no-img-element */

import { cva, cx } from "cva";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
} from "~/landing-pages/components/CTA/cta-shared-styles";
import {
  heroDescriptionFontStyle,
  heroHeadlineFontStyle,
  primaryTextColorStyle,
  secondaryTextColorStyle,
} from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";

export interface HeroWithOffsetImageProps {
  blockIndex: number;
  appearance: "light" | "dark";
  imageUrl: string;
  imageAlt: string;
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
  secondaryCtaLink?: string;
  secondaryCtaText?: string;
}

const containerStyles = cva(["relative isolate overflow-hidden pt-14"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      light: "bg-white bg-gradient-to-b from-brand-indigo-100/20",
      dark: "bg-gray-900",
    },
  },
});

const angleOffsetContainer = cva(
  [
    "absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] shadow-3 ring-1 sm:-mr-80 lg:-mr-96",
  ],
  {
    defaultVariants: {
      appearance: "light",
    },
    variants: {
      appearance: {
        light: "bg-white shadow-brand-indigo-600/10 ring-brand-indigo-50",
        dark: "bg-gray-900 shadow-brand-indigo-100/10 ring-brand-indigo-800",
      },
    },
  }
);

export const HeroWithOffsetImage = ({
  blockIndex,
  appearance,
  imageUrl,
  imageAlt,
  headline,
  description,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
}: HeroWithOffsetImageProps): JSX.Element => {
  const hasSecondaryCTA = !!(secondaryCtaLink && secondaryCtaText);

  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  const secondaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Secondary CTA", {
      ...analyticsVariables,
      linkText: secondaryCtaText,
      linkUrl: secondaryCtaLink,
    });
  return (
    <div className={containerStyles({ appearance })}>
      <div
        className={angleOffsetContainer({ appearance })}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-7xl px-6 py-8 sm:py-12 lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
          <h1
            className={cx(
              heroHeadlineFontStyle(),
              primaryTextColorStyle({ appearance }),
              "max-w-2xl font-bold lg:col-span-2 xl:col-auto"
            )}
          >
            {headline}
          </h1>
          <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
            <div
              className={cx(
                heroDescriptionFontStyle(),
                secondaryTextColorStyle({ appearance })
              )}
            >
              <BuildHTML
                html={description}
                tagToClassNameMap={tagToClassNameMap}
              />
            </div>
            <div className="mt-10 flex items-center gap-x-6">
              <CTALink
                href={primaryCtaLink}
                onClick={primaryButtonClick}
                className={primaryButtonStyle({ appearance })}
              >
                {primaryCtaText}
              </CTALink>
              {hasSecondaryCTA && (
                <CTALink
                  href={secondaryCtaLink}
                  onClick={secondaryButtonClick}
                  className={secondaryButtonStyle({ appearance })}
                >
                  {secondaryCtaText}
                </CTALink>
              )}
            </div>
          </div>
          <img
            src={imageUrl}
            alt={imageAlt}
            className="mt-10 aspect-[6/5] w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
          />
        </div>
      </div>
      {appearance === "light" && (
        <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
      )}
    </div>
  );
};
